import React, { useRef, useMemo } from "react";
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { cryptiqueAnalyticsTheme as themeDefault } from "../theme";
import { formatNumber } from "../../../../shared/utils/numberFormatter";
import { makeFormatValue, getThresholdValues } from "../utils/axisFormat";

function formatMixpanelCohortLabel(raw) {
  const s = String(raw || "").trim();
  // YYYY-MM-DD -> "Aug 21"
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) {
    const y = Number(m[1]);
    const mo = Number(m[2]) - 1;
    const d = Number(m[3]);
    const dt = new Date(y, mo, d);
    if (!Number.isNaN(dt.getTime())) {
      return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(dt);
    }
  }
  // YYYY-MM -> "Aug 2024" (fallback if we ever use month labels)
  const m2 = s.match(/^(\d{4})-(\d{2})$/);
  if (m2) {
    const y = Number(m2[1]);
    const mo = Number(m2[2]) - 1;
    const dt = new Date(y, mo, 1);
    if (!Number.isNaN(dt.getTime())) {
      return new Intl.DateTimeFormat("en-US", { month: "short", year: "numeric" }).format(dt);
    }
  }
  return s || "—";
}

/**
 * Retention chart: "retention-curve" = weighted average vs lag (Day 0…n).
 * "line" = one interval column vs cohort calendar date (Mixpanel-style time series).
 */
export default function RetentionLineChart({
  intervals = [],
  weightedAverage = null,
  cohorts = [],
  breakdowns = [],
  metric = "retention_rate",
  config,
  chartType = "retention-curve",
  height = 280,
}) {
  const containerRef = useRef(null);

  const leftY = config?.axis?.leftY ?? {};
  const xAxis = config?.axis?.xAxis ?? {};
  const yAxisConfig = leftY;
  const formatYValue = makeFormatValue(yAxisConfig);
  const leftYVisible = yAxisConfig.visible !== false;
  const leftYLabel = yAxisConfig.label;
  const leftYScale =
    yAxisConfig && String(yAxisConfig.scale) === "log" ? "log" : "linear";
  const outOfBounds = yAxisConfig.outOfBounds ?? "hidden";
  const { min: minThreshold, max: maxThreshold } = getThresholdValues(yAxisConfig);
  const hasThresholds = minThreshold != null || maxThreshold != null;
  const xAxisVisible = xAxis.visible !== false;
  const xAxisLabel = xAxis.label || "";

  const configColors = config && Array.isArray(config.colors) ? config.colors : [];
  const lineColor = configColors[0] || themeDefault.accentChart || themeDefault.primaryLight || "#7c3aed";
  const breakdownColors = [
    "#7c3aed", "#3b82f6", "#10b981", "#f59e0b",
    "#ef4444", "#8b5cf6", "#06b6d4", "#84cc16",
  ];

  const rawIntervalIdx =
    typeof config?.retentionLineIntervalIndex === "number" && !Number.isNaN(config.retentionLineIntervalIndex)
      ? config.retentionLineIntervalIndex
      : 0;
  const intervalColumnIndex =
    intervals.length > 0 ? Math.min(Math.max(0, rawIntervalIdx), intervals.length - 1) : 0;

  /** Calendar-date line: one retention column per cohort day */
  const calendarSeries = useMemo(() => {
    if (chartType !== "line" || intervals.length === 0) return [];
    const rows = (cohorts || []).filter((c) => c && !c.isSummary);
    const sorted = [...rows].sort((a, b) => String(a.label || "").localeCompare(String(b.label || "")));
    const basePoints = sorted.map((cohort, i) => {
      const values = cohort.values || [];
      const inc = cohort.isIncomplete || [];
      const v =
        values[intervalColumnIndex] !== undefined && values[intervalColumnIndex] !== null
          ? Number(values[intervalColumnIndex])
          : 0;
      const incomplete = Array.isArray(inc) && inc[intervalColumnIndex] === true;
      const cohortSizeRaw = cohort.cohortSize;
      const cohortSizeNum =
        typeof cohortSizeRaw === "number" && !Number.isNaN(cohortSizeRaw) ? cohortSizeRaw : parseInt(cohortSizeRaw, 10);
      const prev = i > 0 ? sorted[i - 1] : null;
      const prevVal =
        prev && prev.values && prev.values[intervalColumnIndex] !== undefined
          ? Number(prev.values[intervalColumnIndex])
          : null;
      const deltaPct =
        prevVal != null && !Number.isNaN(prevVal) && !Number.isNaN(v) ? v - prevVal : null;
      return {
        cohortDateRaw: cohort.label || "—",
        cohortDate: formatMixpanelCohortLabel(cohort.label),
        value: v,
        isIncomplete: incomplete,
        cohortSize: Number.isFinite(cohortSizeNum) ? cohortSizeNum : null,
        deltaFromPrev: deltaPct,
      };
    });
    // Determine boundary between complete and incomplete points (Mixpanel-style dotted tail)
    const firstIncompleteIdx = basePoints.findIndex((p) => p.isIncomplete === true);
    return basePoints.map((p, idx) => {
      const pointIsComplete = p.isIncomplete !== true;
      const bridgeLastComplete =
        firstIncompleteIdx > 0 && idx === firstIncompleteIdx - 1;
      return {
        ...p,
        valueComplete: pointIsComplete ? p.value : null,
        valueIncomplete: !pointIsComplete
          ? p.value
          : bridgeLastComplete
            ? p.value
            : null,
      };
    });
  }, [chartType, cohorts, intervalColumnIndex, intervals.length]);

  /** Calendar-date line with breakdown series (Mixpanel-style multi-line by segment) */
  const calendarSeriesWithBreakdowns = useMemo(() => {
    if (chartType !== "line" || intervals.length === 0) return [];
    const hasBreakdownSeries = Array.isArray(breakdowns) && breakdowns.length > 0;
    if (!hasBreakdownSeries) return [];

    const rows = (cohorts || []).filter((c) => c && !c.isSummary);
    const sorted = [...rows].sort((a, b) => String(a.label || "").localeCompare(String(b.label || "")));

    const segmentCohortMaps = (breakdowns || []).map((bd) => {
      const map = new Map();
      (bd?.cohorts || []).forEach((c) => {
        const key = String(c?.label || "");
        if (key) map.set(key, c);
      });
      return map;
    });

    const basePoints = sorted.map((cohort, i) => {
      const cohortLabelRaw = String(cohort.label || "—");
      const point = {
        cohortDateRaw: cohortLabelRaw,
        cohortDate: formatMixpanelCohortLabel(cohortLabelRaw),
      };

      (breakdowns || []).forEach((bd, bdIndex) => {
        const segCohort = segmentCohortMaps[bdIndex]?.get(cohortLabelRaw) || null;
        const denomRaw = segCohort?.cohortSize;
        const denom =
          typeof denomRaw === "number" && !Number.isNaN(denomRaw) ? denomRaw : parseInt(denomRaw, 10) || 0;
        const retainedRaw = Array.isArray(segCohort?.retained) ? segCohort.retained[intervalColumnIndex] : 0;
        const retained = retainedRaw != null ? Number(retainedRaw) : 0;
        const isCompleteArr = Array.isArray(segCohort?.isComplete) ? segCohort.isComplete : [];
        const isIncomplete = isCompleteArr[intervalColumnIndex] === false;
        const v = metric === "retention_rate" ? (denom > 0 ? (retained / denom) * 100 : 0) : retained;

        point[`breakdown_${bdIndex}`] = v;
        point[`breakdown_${bdIndex}_isIncomplete`] = isIncomplete;
        point[`breakdown_${bdIndex}_users`] = denom || null;

        const prev = i > 0 ? segmentCohortMaps[bdIndex]?.get(String(sorted[i - 1]?.label || "")) : null;
        const prevDenomRaw = prev?.cohortSize;
        const prevDenom =
          typeof prevDenomRaw === "number" && !Number.isNaN(prevDenomRaw) ? prevDenomRaw : parseInt(prevDenomRaw, 10) || 0;
        const prevRetainedRaw = Array.isArray(prev?.retained) ? prev.retained[intervalColumnIndex] : 0;
        const prevRetained = prevRetainedRaw != null ? Number(prevRetainedRaw) : 0;
        const prevV = metric === "retention_rate" ? (prevDenom > 0 ? (prevRetained / prevDenom) * 100 : 0) : prevRetained;
        point[`breakdown_${bdIndex}_deltaFromPrev`] =
          i > 0 && Number.isFinite(prevV) && Number.isFinite(v) ? v - prevV : null;
      });

      return point;
    });

    // Add Mixpanel-style dotted tails per breakdown segment
    return basePoints.map((p, idx) => {
      const out = { ...p };
      (breakdowns || []).forEach((_, bdIndex) => {
        const isIncKey = `breakdown_${bdIndex}_isIncomplete`;
        const valKey = `breakdown_${bdIndex}`;
        const pointIsComplete = out[isIncKey] !== true;
        const firstIncompleteIdx = basePoints.findIndex((bp) => bp[`breakdown_${bdIndex}_isIncomplete`] === true);
        const bridgeLastComplete = firstIncompleteIdx > 0 && idx === firstIncompleteIdx - 1;
        out[`breakdown_${bdIndex}_complete`] = pointIsComplete ? out[valKey] : null;
        out[`breakdown_${bdIndex}_incomplete`] = !pointIsComplete ? out[valKey] : bridgeLastComplete ? out[valKey] : null;
      });
      return out;
    });
  }, [chartType, cohorts, breakdowns, intervalColumnIndex, intervals.length, metric]);

  if (chartType === "line") {
    const hasBreakdownSeries = Array.isArray(breakdowns) && breakdowns.length > 0;
    const dataForLine = hasBreakdownSeries ? calendarSeriesWithBreakdowns : calendarSeries;

    if (dataForLine.length === 0) {
      return (
        <div className="flex items-center justify-center" style={{ height }}>
          <div className="text-sm" style={{ color: themeDefault.textMuted }}>
            No cohort data for line view
          </div>
        </div>
      );
    }

    let yDomainLine = [0, 100];
    if (metric === "retention_rate") {
      if (hasThresholds && outOfBounds === "hidden") {
        yDomainLine = [
          minThreshold != null ? minThreshold : 0,
          maxThreshold != null ? maxThreshold : 100,
        ];
      }
    } else {
      const maxV = hasBreakdownSeries
        ? Math.max(
            0,
            ...dataForLine.flatMap((row) =>
              (breakdowns || []).map((_, i) => Number(row?.[`breakdown_${i}`] || 0))
            )
          )
        : Math.max(...dataForLine.map((d) => d.value || 0), 0);
      yDomainLine = [0, maxV * 1.1 || 1];
    }

    const intervalLabel = intervals[intervalColumnIndex] || "Interval";
    const lineName = metric === "retention_rate" ? `Retention (${intervalLabel})` : intervalLabel;

    const CalendarTooltip = ({ active, payload }) => {
      if (!active || !payload || payload.length === 0) return null;
      const row = payload[0]?.payload;
      if (!row) return null;
      const title = row.cohortDate;
      return (
        <div
          style={{
            backgroundColor: "white",
            border: `1px solid ${themeDefault.border}`,
            borderRadius: "8px",
            padding: "10px 12px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          <div style={{ marginBottom: "6px", fontWeight: 600, fontSize: "12px", color: themeDefault.text }}>
            {title}
          </div>

          {hasBreakdownSeries ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              {(breakdowns || []).map((bd, i) => {
                const v = row?.[`breakdown_${i}`];
                if (v == null) return null;
                const users = row?.[`breakdown_${i}_users`];
                const delta = row?.[`breakdown_${i}_deltaFromPrev`];
                const line = metric === "retention_rate" ? `${Number(v).toFixed(2)}% retention` : formatNumber(v);
                return (
                  <div key={i} style={{ fontSize: "11px", color: themeDefault.text }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", color: themeDefault.textMuted }}>
                      <span
                        style={{
                          display: "inline-block",
                          width: "8px",
                          height: "8px",
                          backgroundColor: breakdownColors[i % breakdownColors.length],
                          borderRadius: "2px",
                        }}
                      />
                      <span>{bd?.segment || `Breakdown ${i + 1}`}</span>
                    </div>
                    <div style={{ marginTop: "2px" }}>
                      {line}
                      {users != null ? `, ${formatNumber(users)} profile${users === 1 ? "" : "s"}` : ""}
                      {delta != null && metric === "retention_rate" ? `, ${delta >= 0 ? "+" : ""}${Number(delta).toFixed(2)}% from previous cohort day` : ""}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "11px", color: themeDefault.textMuted }}>
                <span
                  style={{
                    display: "inline-block",
                    width: "8px",
                    height: "8px",
                    backgroundColor: lineColor,
                    borderRadius: "2px",
                  }}
                />
                <span>{intervalLabel}</span>
              </div>
              <div style={{ marginTop: "4px", fontSize: "11px", color: themeDefault.text }}>
                {metric === "retention_rate"
                  ? `${Number(row.value).toFixed(2)}% retention`
                  : formatNumber(row.value)}
              </div>
              {row.cohortSize != null && (
                <div style={{ marginTop: "2px", fontSize: "11px", color: themeDefault.text }}>
                  {formatNumber(row.cohortSize)} profile{row.cohortSize === 1 ? "" : "s"}
                </div>
              )}
              {row.deltaFromPrev != null && metric === "retention_rate" && (
                <div style={{ marginTop: "4px", fontSize: "11px", color: themeDefault.textMuted }}>
                  {row.deltaFromPrev >= 0 ? "+" : ""}
                  {Number(row.deltaFromPrev).toFixed(2)}% from previous cohort day
                </div>
              )}
              {row.isIncomplete && (
                <div style={{ marginTop: "6px", fontSize: "11px", color: themeDefault.textMuted }}>
                  Incomplete period
                </div>
              )}
            </>
          )}
        </div>
      );
    };

    return (
      <div ref={containerRef} style={{ width: "100%", height }}>
        <ResponsiveContainer width="100%" height="100%">
          <RechartsLineChart data={dataForLine} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={themeDefault.border} />
            <XAxis
              dataKey="cohortDate"
              tick={{ fontSize: 10, fill: themeDefault.textMuted }}
              stroke={themeDefault.border}
              hide={!xAxisVisible}
              interval="preserveStartEnd"
              label={
                xAxisLabel
                  ? {
                      value: xAxisLabel,
                      position: "insideBottom",
                      offset: -5,
                      style: { textAnchor: "middle", fontSize: 11, fill: themeDefault.textMuted },
                    }
                  : null
              }
            />
            <YAxis
              tick={{ fontSize: 11, fill: themeDefault.textMuted }}
              stroke={themeDefault.border}
              hide={!leftYVisible}
              label={
                leftYLabel
                  ? {
                      value: leftYLabel,
                      angle: -90,
                      position: "insideLeft",
                      style: { textAnchor: "middle", fontSize: 11, fill: themeDefault.textMuted },
                    }
                  : null
              }
              domain={yDomainLine}
              scale={leftYScale}
              tickFormatter={(value) => formatYValue(value)}
            />
            <Tooltip content={<CalendarTooltip />} />
            {leftYVisible && <Legend />}
            {hasBreakdownSeries ? (
              (breakdowns || []).map((bd, i) => (
                <React.Fragment key={i}>
                  <Line
                    type="monotone"
                    dataKey={`breakdown_${i}_complete`}
                    name={bd?.segment || `Breakdown ${i + 1}`}
                    stroke={breakdownColors[i % breakdownColors.length]}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                    connectNulls={false}
                  />
                  <Line
                    type="monotone"
                    dataKey={`breakdown_${i}_incomplete`}
                    name={`${bd?.segment || `Breakdown ${i + 1}`} (Incomplete)`}
                    stroke={breakdownColors[i % breakdownColors.length]}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 5 }}
                    connectNulls={false}
                    strokeDasharray="3 4"
                    legendType="none"
                  />
                </React.Fragment>
              ))
            ) : (
              <>
                <Line
                  type="monotone"
                  dataKey="valueComplete"
                  name={lineName}
                  stroke={lineColor}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5, fill: lineColor, stroke: "#fff", strokeWidth: 2 }}
                  connectNulls={false}
                />
                <Line
                  type="monotone"
                  dataKey="valueIncomplete"
                  name={`${lineName} (Incomplete)`}
                  stroke={lineColor}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 5, fill: lineColor, stroke: "#fff", strokeWidth: 2 }}
                  connectNulls={false}
                  strokeDasharray="3 4"
                  legendType="none"
                />
              </>
            )}
          </RechartsLineChart>
        </ResponsiveContainer>
      </div>
    );
  }

  // --- Retention curve (weighted average vs lag) — unchanged logic ---
  const completeness =
    weightedAverage?.isComplete && Array.isArray(weightedAverage.isComplete)
      ? intervals.map((_, i) => weightedAverage.isComplete[i] !== false)
      : intervals.map(() => true);
  const firstIncompleteIdx = completeness.findIndex((c) => !c);

  const chartData = intervals.map((intervalLabel, index) => {
    const pointIsComplete = completeness[index];
    const dataPoint = {
      interval: intervalLabel,
      index,
      isComplete: pointIsComplete,
    };

    if (weightedAverage && weightedAverage.values && weightedAverage.values[index] !== undefined) {
      const v = weightedAverage.values[index];
      dataPoint.weightedAverage = v;
      dataPoint.weightedAverageComplete = pointIsComplete ? v : null;
      const bridgeLastComplete = firstIncompleteIdx > 0 && index === firstIncompleteIdx - 1;
      dataPoint.weightedAverageIncomplete = !pointIsComplete
        ? v
        : bridgeLastComplete
          ? v
          : null;
      dataPoint.avgUsers =
        weightedAverage.avgUsers && weightedAverage.avgUsers[index] !== undefined
          ? weightedAverage.avgUsers[index]
          : null;
    }

    if (breakdowns && breakdowns.length > 0) {
      breakdowns.forEach((breakdown, bdIndex) => {
        if (breakdown.values && breakdown.values[index] !== undefined) {
          dataPoint[`breakdown_${bdIndex}`] = breakdown.values[index];
        }
      });
    }

    return dataPoint;
  });

  if (chartData.length === 0) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <div className="text-sm" style={{ color: themeDefault.textMuted }}>
          No data available
        </div>
      </div>
    );
  }

  const hasBreakdownSeries = Array.isArray(breakdowns) && breakdowns.length > 0;
  const getBreakdownAvgUsersAt = (breakdownMeta, idx) => {
    if (!breakdownMeta || idx == null) return null;
    const candidates = [
      breakdownMeta.avgUsers,
      breakdownMeta.averageUsers,
      breakdownMeta.avg_users,
      breakdownMeta.users,
    ];
    for (const arr of candidates) {
      if (Array.isArray(arr) && arr[idx] !== undefined && arr[idx] !== null) {
        return arr[idx];
      }
    }
    return null;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;
    const point = payload[0]?.payload;
    const isComplete = point?.isComplete !== false;
    const primaryEntry =
      payload.find((p) => String(p?.dataKey || "").startsWith("weightedAverage")) || payload[0];

    const primaryValue = primaryEntry?.value;
    const primaryColor = primaryEntry?.color || lineColor;
    const avgUsers = point?.avgUsers;

    const title =
      typeof label === "string" && /^Day\s+\d+$/i.test(label)
        ? label
        : typeof label === "string" && /^[0-9]+$/.test(label)
          ? `Day ${label}`
          : label;

    const metricLine =
      metric === "retention_rate"
        ? `${formatNumber(primaryValue, { decimals: 2 })}% retention`
        : `${formatNumber(primaryValue)} users`;

    const seriesLabel = isComplete
      ? "Weighted Average Value in completed intervals"
      : "Weighted Average Value in incomplete intervals";

    const breakdownEntries = hasBreakdownSeries
      ? payload
          .filter((p) => String(p?.dataKey || "").startsWith("breakdown_"))
          .map((entry) => {
            const breakdownKey = String(entry?.dataKey || "");
            const breakdownIndex = Number(breakdownKey.replace("breakdown_", ""));
            const breakdownMeta =
              Number.isInteger(breakdownIndex) && breakdownIndex >= 0 ? breakdowns[breakdownIndex] : null;
            const users = getBreakdownAvgUsersAt(breakdownMeta, point?.index);
            return {
              key: breakdownKey,
              color: entry?.color || breakdownColors[breakdownIndex % breakdownColors.length] || lineColor,
              label: breakdownMeta?.segment || `Breakdown ${breakdownIndex + 1}`,
              value: entry?.value,
              users,
            };
          })
          .filter((entry) => entry.value != null)
      : [];

    return (
      <div
        style={{
          backgroundColor: "white",
          border: `1px solid ${themeDefault.border}`,
          borderRadius: "8px",
          padding: "10px 12px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <div style={{ marginBottom: "6px", fontWeight: 600, fontSize: "12px", color: themeDefault.text }}>
          {title}
        </div>

        {hasBreakdownSeries ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            {breakdownEntries.map((entry) => (
              <div key={entry.key} style={{ fontSize: "11px", color: themeDefault.text }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", color: themeDefault.textMuted }}>
                  <span
                    style={{
                      display: "inline-block",
                      width: "8px",
                      height: "8px",
                      backgroundColor: entry.color,
                      borderRadius: "2px",
                    }}
                  />
                  <span>{entry.label}</span>
                </div>
                <div style={{ marginTop: "2px" }}>
                  {metric === "retention_rate"
                    ? `${formatNumber(entry.value, { decimals: 2 })}% retention`
                    : `${formatNumber(entry.value)} users`}
                  {entry.users != null ? `, ${formatNumber(entry.users, { decimals: 4 })} users (average)` : ""}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "11px", color: themeDefault.textMuted }}>
              <span
                style={{
                  display: "inline-block",
                  width: "8px",
                  height: "8px",
                  backgroundColor: primaryColor,
                  borderRadius: "2px",
                }}
              />
              <span>{seriesLabel}</span>
            </div>

            <div style={{ marginTop: "4px", fontSize: "11px", color: themeDefault.text }}>
              {metricLine}
            </div>

            {avgUsers != null && (
              <div style={{ marginTop: "2px", fontSize: "11px", color: themeDefault.text }}>
                {formatNumber(avgUsers, { decimals: 4 })} users (average)
              </div>
            )}
          </>
        )}

        {!isComplete && (
          <div style={{ marginTop: "6px", fontSize: "11px", color: themeDefault.textMuted }}>
            Incomplete period
          </div>
        )}
      </div>
    );
  };

  let yDomain = [0, 100];
  if (metric === "retention_rate") {
    if (hasThresholds && outOfBounds === "hidden") {
      yDomain = [
        minThreshold != null ? minThreshold : 0,
        maxThreshold != null ? maxThreshold : 100,
      ];
    }
  } else {
    const maxValue = Math.max(
      ...chartData.map((d) => d.weightedAverage || 0),
      ...(breakdowns || []).flatMap((bd) => bd.values || [])
    );
    yDomain = [0, maxValue * 1.1];
  }

  return (
    <div ref={containerRef} style={{ width: "100%", height }}>
      <ResponsiveContainer width="100%" height="100%">
        <RechartsLineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={themeDefault.border} />
          <XAxis
            dataKey="interval"
            tick={{ fontSize: 11, fill: themeDefault.textMuted }}
            stroke={themeDefault.border}
            hide={!xAxisVisible}
            label={
              xAxisLabel
                ? {
                    value: xAxisLabel,
                    position: "insideBottom",
                    offset: -5,
                    style: { textAnchor: "middle", fontSize: 11, fill: themeDefault.textMuted },
                  }
                : null
            }
          />
          <YAxis
            tick={{ fontSize: 11, fill: themeDefault.textMuted }}
            stroke={themeDefault.border}
            hide={!leftYVisible}
            label={
              leftYLabel
                ? {
                    value: leftYLabel,
                    angle: -90,
                    position: "insideLeft",
                    style: { textAnchor: "middle", fontSize: 11, fill: themeDefault.textMuted },
                  }
                : null
            }
            domain={yDomain}
            scale={leftYScale}
            tickFormatter={(value) => formatYValue(value)}
          />
          <Tooltip content={<CustomTooltip />} />
          {leftYVisible && <Legend />}

          {weightedAverage && !hasBreakdownSeries && (
            <>
              <Line
                type="monotone"
                dataKey="weightedAverageComplete"
                name="Weighted Average"
                stroke={lineColor}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 5, fill: lineColor, stroke: "#fff", strokeWidth: 2 }}
                connectNulls={false}
              />
              <Line
                type="monotone"
                dataKey="weightedAverageIncomplete"
                name="Weighted Average (Incomplete)"
                stroke={lineColor}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 5, fill: lineColor, stroke: "#fff", strokeWidth: 2 }}
                connectNulls={false}
                strokeDasharray="3 4"
                legendType="none"
              />
            </>
          )}

          {breakdowns && breakdowns.length > 0 && breakdowns.map((breakdown, index) => (
            <Line
              key={index}
              type="monotone"
              dataKey={`breakdown_${index}`}
              name={breakdown.segment || `Breakdown ${index + 1}`}
              stroke={breakdownColors[index % breakdownColors.length]}
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
            />
          ))}
        </RechartsLineChart>
      </ResponsiveContainer>
    </div>
  );
}
